import React, { useEffect, useRef, useState } from 'react';
import styles from './RevenueVendorExpenseTable.module.scss';
import Headers from './components/Header';
import Rows from './components/Rows';
import InfiniteScroll from 'react-infinite-scroll-component';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { fetchRevenueVendorExpenseTableData } from '../../../../../store/dashboard/revenueVendorExpenses/revenueVendorExpensesApiHelpers';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { infoIconGreen, overlapInfoIconGrey } from '../../../../../assets/hostedassets';
import clsx from 'clsx';
import { infoTooltipB2b, infoTooltTipVendorExpenses } from '../GSTR3BTable/gstr3Constants';
import HeaderBodyBox from '../../../../../common/HeaderBodyBox/HeaderBodyBox';
import { SORT_DIRECTIONS, SortConfig } from '../../../../../utils/constants/commonConstants';
import useRevenueTableRows, { RevenueTableData } from './useRevenueTableRows';

function RevenueVendorExpenseTable({ tab = '', dataSource }) {
    const [fetchingData, setFetchingData] = useState(false);
    const [tableData, setTableData] = useState<RevenueTableData>({
        fetchSuccess: false,
        headers: [],
        rows: [],
        totalRecords: 0,
        records: 0,
    });
    const [pageNum, setPageNum] = useState(1);
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
    const tableRef = useRef(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    // Column sort (Total + month columns) — cycle none → ASC → DESC → none on the shared
    // SortConfig shape; switching to a different column starts it at ASC.
    const onSortColumn = (field: string) =>
        setSortConfig((c) => {
            if (c?.field !== field) return { field, direction: SORT_DIRECTIONS.ASC };
            const next =
                c.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : c.direction === SORT_DIRECTIONS.DESC
                    ? null
                    : SORT_DIRECTIONS.ASC;
            return next ? { field, direction: next } : undefined;
        });

    const noDataAvailable = tableData.fetchSuccess && !tableData?.rows?.length;
    const noGSTConnected = parseInt(dataSource?.[1]?.text?.[0]) === 0;

    const isRevenue = tab === 'revenue';
    // Derive the B2B display headers + rows (Circular/Net columns, Intra + Net B2B Revenue rows,
    // Total-column sort). Pure logic lives in useRevenueTableRows so the money math is unit-tested.
    const { displayHeaders, displayRows } = useRevenueTableRows(tableData, isRevenue, sortConfig);

    const dataSourceList = dataSource?.[1]?.gstList || [];

    useEffect(() => {
        fetchTableData();
    }, []);

    // On first load, land on the LATEST month: months run oldest→newest left-to-right with the
    // pinned Total/Circular/Net at the right, so start the horizontal scroll at the right end.
    const initScrolled = useRef(false);
    useEffect(() => {
        if (initScrolled.current || !tableData?.rows?.length) return;
        const el = scrollContainerRef.current;
        if (el) {
            el.scrollLeft = el.scrollWidth;
            initScrolled.current = true;
        }
    }, [tableData?.rows?.length]);

    const fetchTableData = () => {
        if (pageNum < 2) setFetchingData(true);
        fetchRevenueVendorExpenseTableData(
            {
                gstType: tab === 'revenue' ? 'REVENUE' : tab === 'vendorExpenses' ? 'EXPENSE' : '',
                excludeGstR3: tab === 'revenue',
                pageNum: pageNum,
                pageSize: 50,
            },
            {
                onSuccess: onDataFetchSuccess,
                onError: () => setFetchingData(false),
            },
        );
    };

    const onDataFetchSuccess = (data) => {
        if (pageNum > 1) {
            setTableData((tableData) => {
                const newRows = data?.rows || [];
                if (newRows?.length) {
                    tableData?.rows?.pop();
                    return {
                        ...tableData,
                        rows: [...tableData.rows, ...newRows],
                    };
                } else return tableData;
            });
        } else
            setTableData({
                fetchSuccess: true,
                ...data,
            });
        setFetchingData(false);
        setPageNum((pagenum) => pagenum + 1);
    };

    const getTitle = () => {
        switch (tab) {
            case 'revenue':
                return 'B2B Customer Summary';
            case 'vendorExpenses':
                return 'Vendor Expense From GST';
        }
    };

    // const getSecondaryText = () => {
    //     switch (tab) {
    //         case 'revenue':
    //             return '(Data from GSTR-1)';
    //         case 'vendorExpenses':
    //             return '(Data from GSTR-2)';
    //     }
    // };

    const getDataSource = () => {
        if (tab !== 'revenue') return <></>;
        const connectedGsts = dataSourceList.filter((gst) => gst?.connected);
        return (
            <div className={styles.DataSource}>
                <div className={styles.Content}>
                    Data Source :&nbsp;
                    <div
                        className={clsx(styles.DataSourceBadge, {
                            [styles.Green]: connectedGsts?.length >= dataSourceList?.length,
                        })}
                    >
                        {connectedGsts?.length} / {dataSourceList?.length} GST Connected
                        <Tooltip content={getGSTList()} direction={'left'}>
                            <img src={infoIconGreen} alt="" height="14px" />
                        </Tooltip>
                    </div>
                </div>
            </div>
        );
    };

    const getGSTList = () => {
        return (
            <div className={styles.GstList}>
                {dataSourceList.map((gst) => (
                    <div key={gst?.gstNumber} className={styles.GstItem}>
                        <div>
                            <span>{gst?.gstNumber}</span>
                            {/*<span>{gst?.gstStatus}</span>*/}
                        </div>
                        <div
                            className={clsx(styles.Status, {
                                [styles.Connected]: gst?.connected,
                            })}
                        >
                            {gst?.connected ? 'Connected' : 'Not Connected'}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderHeaderRight = () => {
        if (fetchingData) {
            return <LoadingSpinner color={'#6021b3'} height={'20px'} />;
        }
        if (noDataAvailable) {
            return 'No Data Available';
        }
        return getDataSource();
    };

    const renderTableBody = () => {
        if (noDataAvailable || fetchingData || !tableData?.rows?.length) {
            return null;
        }

        return (
            <div className={styles.TableContainer} id="revenue-table-scroll-container" ref={scrollContainerRef}>
                <InfiniteScroll
                    dataLength={tableData?.rows?.length}
                    hasMore={tableData.rows?.length + 1 < tableData.totalRecords}
                    next={fetchTableData}
                    loader={false}
                    scrollableTarget="revenue-table-scroll-container"
                    style={{ overflow: 'visible' }}
                >
                    <table className={styles.BankTable}>
                        <thead className={styles.TableHead}>
                            <Headers
                                data={displayHeaders}
                                sortConfig={isRevenue ? sortConfig : undefined}
                                onSort={isRevenue ? onSortColumn : undefined}
                            />
                        </thead>
                        <tbody className={styles.TableBody}>
                            <Rows rows={displayRows} headers={displayHeaders} tableRef={tableRef} />
                        </tbody>
                    </table>
                </InfiniteScroll>
            </div>
        );
    };

    return (
        <div className={styles.RevenueVendorExpenseTable}>
            <HeaderBodyBox
                title={
                    <div className={styles.Title}>
                        <span className={styles.Main}>{getTitle()}</span>
                        <Tooltip content={tab === 'revenue' ? infoTooltipB2b : infoTooltTipVendorExpenses} direction={'right'}>
                            <img src={overlapInfoIconGrey} alt="info" height={'18px'} />
                        </Tooltip>
                    </div>
                }
                headerRight={renderHeaderRight()}
                className={clsx({
                    [styles.TopNoData]: fetchingData || noDataAvailable,
                })}
            >
                {renderTableBody()}
            </HeaderBodyBox>
        </div>
    );
}

export default RevenueVendorExpenseTable;
